<template>
  <div :class="$style.modal">
    <div :class="$style.container">
      <div :class="[$style.container__header, $style.header]">
        <div :class="$style.header__title">日程の選択肢を選ぶ</div>
        <ButtonIconOnly
          :class="$style.header__close"
          @click="emit('onCloseClicked')"
          ><template #icon>
            <IconClose />
          </template>
        </ButtonIconOnly>
      </div>

      <div :class="[$style.container__body, $style.body]">
        <div :class="[$style.body__timetable, $style.timetable]">
          <TimeSlotPickerTimeTable
            :class="$style.timetable__component"
            v-model="pickedTimeSlots"
            @onNextClicked="next"
            @onPrevClicked="prev"
            :columns="timeSlotColumns"
            :hasPrev="hasPrev"
          />
          <div :class="$style.timetable__loading" v-show="isLoading">
            <LoadingIndicator />
          </div>
        </div>

        <TimeSlotPickerPickedTimeSlots
          :class="[$style.body__pickedslots]"
          v-model="pickedTimeSlots"
          v-model:fallbackUrl="fallbackUrl"
          @onDone="emit('onPickingTimeSlotDoneClicked')"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import IconClose from '@/components/icons/IconClose.vue'
import { defineResponseHandlers } from '@/libs/utilities/graphql'
import ButtonIconOnly from '@/components/atoms/ButtonIconOnly.vue'
import TimeSlotPickerPickedTimeSlots from '@/components/organisms/TimeSlotPickerModal/TimeSlotPickerPickedTimeSlots.vue'
import { useAlert } from '@/libs/alertPortal'
import { isSameDay } from 'date-fns'
import { computed, watchEffect, ref } from 'vue'
import LoadingIndicator from '../../atoms/LoadingIndicator.vue'
import {
  useTimeSlotPickerModalAvailableBookingRequestTimeSlotsQuery,
  type TimeSlotPickerModalAvailableBookingRequestTimeSlotsQuery,
} from './TimeSlotPickerModal.generated'
import TimeSlotPickerTimeTable, {
  type Column,
} from './TimeSlotPickerTimeTable.vue'
import { type PickedTimeSlots } from './useTimeSlot'
import { useTimeSlotPickerQueryVars } from './useTimeSlotPickerQueryVars'

export type AvailableBookingRequestTimeSlotsError =
  | 'AvailableBookingRequestTimeSlotsErrorNoCalendarConnected'
  | 'AvailableBookingRequestTimeSlotsErrorInvalidCalendarConnection'

const props = defineProps<{
  modelValue: PickedTimeSlots
  fallbackUrl?: string
}>()

const emit = defineEmits<{
  (e: 'onCloseClicked'): void
  (e: 'onPickingTimeSlotDoneClicked'): void
  (e: 'onPickingTimeSlotDone'): void
  (e: 'onCalendarAuthError', err: AvailableBookingRequestTimeSlotsError): void
  (e: 'update:modelValue', picked: PickedTimeSlots): void
  (e: 'update:fallbackUrl', picked: Maybe<string>): void
}>()

const { error } = useAlert()

const fallbackUrl = computed({
  get(): Maybe<string> {
    return props.fallbackUrl
  },
  set(v: Maybe<string>) {
    emit('update:fallbackUrl', v)
  },
})

const pickedTimeSlots = computed({
  get() {
    return props.modelValue
  },
  set(v) {
    emit('update:modelValue', v)
  },
})

const numOfColumns = 4
const { range, daysInRange, hasPrev, next, prev } =
  useTimeSlotPickerQueryVars(numOfColumns)

const query = useTimeSlotPickerModalAvailableBookingRequestTimeSlotsQuery(
  range,
  {
    fetchPolicy: 'network-only',
  }
)

const isLoading = computed(() => query.loading.value)

const parseTimeSlots = (
  timeSlots: Extract<
    TimeSlotPickerModalAvailableBookingRequestTimeSlotsQuery['availableBookingRequestTimeSlots'],
    {
      __typename: 'AvailableBookingRequestTimeSlotsSuccess'
    }
  >['timeSlots']
): Column[] => {
  return daysInRange.value.map(
    (d): Column => ({
      date: d,
      availableTimeSlots: (() => {
        if (!timeSlots) {
          return []
        }

        return timeSlots.filter(timeSlot => {
          return isSameDay(new Date(timeSlot.startsAt), d)
        })
      })(),
    })
  )
}

const timeSlotColumns = ref<Column[]>([])

watchEffect(() => {
  if (query.error.value) {
    error(`エラーが発生しました。暫くしてからもう一度お試しください。`)
  }

  if (!query.result.value) {
    return
  }

  defineResponseHandlers<
    TimeSlotPickerModalAvailableBookingRequestTimeSlotsQuery['availableBookingRequestTimeSlots'],
    void
  >({
    AvailableBookingRequestTimeSlotsErrorNoCalendarConnected: ({
      __typename,
    }) => {
      emit('onCalendarAuthError', __typename)
    },
    AvailableBookingRequestTimeSlotsErrorInvalidCalendarConnection: ({
      __typename,
    }) => {
      emit('onCalendarAuthError', __typename)
    },
    AvailableBookingRequestTimeSlotsSuccess: payload => {
      timeSlotColumns.value = parseTimeSlots(payload.timeSlots)
    },
  })(query.result.value.availableBookingRequestTimeSlots)
})
</script>

<style module lang="scss">
.icon {
  font-size: 24px;
}

.modal {
  width: 80vw;
  height: 80vh;
  overflow: auto;
}

.container {
  width: 100%;
  height: 100%;
  min-width: 800px;
  min-height: 500px;
  display: flex;
  flex-direction: column;

  &__header {
    padding: 12px 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid $color-border-low-emphasis;
  }

  &__body {
    flex: 1;
  }
}

.header {
  &__title {
    font-weight: 700;
    font-size: 20px;
  }

  &__close {
    font-size: 24px;
  }
}

.body {
  display: flex;
  align-items: stretch;

  &__timetable {
    flex: 4;
  }

  &__pickedslots {
    flex: 1;
    border-left: 1px solid $color-border-low-emphasis;
  }
}

.timetable {
  position: relative;

  &__component {
    height: 100%;
  }

  &__loading {
    position: absolute;
    display: flex;
    justify-content: center;
    align-items: center;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    z-index: 1;
    background-color: $color-surface-primary;
  }
}
</style>
